import { InboxOutlined, UploadOutlined } from "@ant-design/icons";
import { useUploadGstFile } from "@entities/financeApproval/lib/hooks/useUploadGstFile";
import { FirebaseService, ToastService } from "@shared/lib/services";
import { Button, DatePicker, message, Space, Typography, Upload } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import styles from "./styles.module.scss";

const { Dragger } = Upload;
const { Text } = Typography;

export function UploadGstBilling({ onClose }: { onClose: () => void }) {
  const {
    uploadGstFile,
    isUploading: isUploadingGstFile,
    isUploaded: isUploadedGstFile,
  } = useUploadGstFile();
  const [selectedDate, setSelectedDate] = useState<string | null>(
    dayjs().startOf("month").startOf("day").toISOString()
  );
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string>("");

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    setSelectedDate(
      date?.startOf("month").startOf("day").toISOString() || null
    );
  };

  const handleFileUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const uploadedUrl = await FirebaseService.uploadFileToFirebase(
        "gst-billing",
        file
      );
      setUploadedFileUrl(uploadedUrl);
      setUploadedFile(file);
      ToastService.success("GST billing file uploaded successfully");
    } catch (error) {
      ToastService.error("File upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    accept: ".xlsx,.xls,.csv,text/csv",
    beforeUpload: (file: File) => {
      const isValidType = [
        ".xlsx",
        ".xls",
        ".csv",
        "text/csv",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ].includes(file.type);

      if (!isValidType) {
        message.error("You can only upload XLSX, XLS, CSV files!");
        return false;
      }

      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error("File must be smaller than 10MB!");
        return false;
      }

      handleFileUpload(file);
      return false; // Prevent default upload
    },
    showUploadList: false,
  };

  const handleSubmit = () => {
    if (!selectedDate) {
      ToastService.error("Please select a date");
      return;
    }
    if (!uploadedFileUrl) {
      ToastService.error("Please upload a file");
      return;
    }

    uploadGstFile({
      invoiceDate: {
        from: dayjs(selectedDate).startOf("month").startOf("day").toISOString(),
        to: dayjs(selectedDate).endOf("month").endOf("day").toISOString(),
      },
      inputFileUrl: uploadedFileUrl,
    });
  };

  useEffect(() => {
    if (isUploadedGstFile) {
      ToastService.success("GST billing data submitted successfully");
      setSelectedDate(null);
      setUploadedFile(null);
      setUploadedFileUrl("");
      onClose();
    }
  }, [isUploadedGstFile]);

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* Date Picker */}
        <div className={styles.dateSection}>
          <Text strong>Select Date:</Text>
          <DatePicker
            value={selectedDate ? dayjs(selectedDate) : null}
            onChange={handleDateChange}
            placeholder="Select date"
            style={{ width: "100%", marginTop: 8 }}
            picker="month"
            format="MMM YYYY"
          />
        </div>

        {/* File Upload */}
        <div className={styles.uploadSection}>
          <Text strong>
            Upload GST Billing File:
            <Button
              type="link"
              onClick={() => {
                window.open(
                  "https://docs.google.com/spreadsheets/d/1MRpVpq4OTUA2BJTYVlYJS8zDXZW1TgasuP4BmqwC5ko/edit?gid=0#gid=0",
                  "_blank"
                );
              }}
            >
              View Template
            </Button>
          </Text>

          <Dragger
            {...uploadProps}
            className={styles.uploadArea}
            disabled={isUploading}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              {isUploading
                ? "Uploading..."
                : uploadedFile
                ? `File: ${uploadedFile.name}`
                : "Click or drag file to this area to upload"}
            </p>
            <p className="ant-upload-hint">
              {isUploading
                ? "Please wait while your file is being uploaded"
                : "Support for single file upload (PDF)"}
            </p>
          </Dragger>
        </div>

        {/* Submit Button */}
        <div className={styles.actions}>
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={handleSubmit}
            disabled={
              !selectedDate ||
              !uploadedFileUrl ||
              isUploading ||
              isUploadingGstFile
            }
            loading={isUploading || isUploadingGstFile}
            size="large"
          >
            Submit GST Billing
          </Button>
        </div>
      </Space>
    </div>
  );
}

export default UploadGstBilling;
